import Cookies from 'universal-cookie';

import { TTrackingMeta } from 'shared/domain/entities';
import { GOOGLE_CLICK_ID_PARAMS } from 'shared/presentation/constants';

import getCurrentIp from './getCurrentIp';
import getGoogleClickIdentifier from './getGoogleClickIdentifier';
import getSessionReferrer from './getSessionReferrer';

type TParams = {
  event?: string;
};

/**
 * Os `ltk_*` de `UTM_QUERY_PARAMS` ficam de fora: não têm destino no cadastro
 * do cliente e enviá-los significaria mandar chave desconhecida ao Vectis.
 */
const UTM_TO_TRACKING_FIELD = {
  utm_source: 'source',
  utm_medium: 'medium',
  utm_campaign: 'campaign',
  utm_term: 'term',
  utm_content: 'content',
  fbclid: 'facebookId',
} as const;

type TTrackedUtmParam = keyof typeof UTM_TO_TRACKING_FIELD;
type TTrackedUtms = Pick<
  TTrackingMeta,
  'source' | 'medium' | 'campaign' | 'term' | 'content' | 'facebookId'
>;

/**
 * Monta o bloco de atribuição de campanha enviado junto ao cadastro do cliente
 * (ED-7201).
 *
 * A fonte primária é o cookie, não a query string: `AppContext` grava as UTMs
 * na carga do app a partir da URL de entrada original (ED-6914), então quem
 * chega com UTM na home e só depois navega até o cadastro continua atribuído.
 * A URL atual entra como fallback, para o caso de a gravação do cookie não ter
 * acontecido (cookies bloqueados, entrada direta na tela de cadastro).
 */
export default async function getRegistrationTrackingMeta({
  event,
}: TParams = {}): Promise<TTrackingMeta> {
  const cookies = new Cookies();
  const urlParams = new URLSearchParams(window.location.search);

  const readAttributionParam = (param: string) => {
    const fromCookie = cookies.get(param);
    if (typeof fromCookie === 'string' && fromCookie) return fromCookie;

    return urlParams.get(param) || undefined;
  };

  const utms = (
    Object.keys(UTM_TO_TRACKING_FIELD) as TTrackedUtmParam[]
  ).reduce((acc, param) => {
    const value = readAttributionParam(param);
    if (!value) return acc;

    return { ...acc, [UTM_TO_TRACKING_FIELD[param]]: value };
  }, {} as TTrackedUtms);

  const { googleId, googleIdType } = getGoogleClickIdentifier(
    GOOGLE_CLICK_ID_PARAMS.map(param => [param, readAttributionParam(param)]),
  );

  const referrer = getSessionReferrer();
  const ip = await getCurrentIp();

  return {
    ...utms,
    ...(googleId ? { googleId, googleIdType } : {}),
    ...(referrer ? { referrer } : {}),
    ...(event ? { nomeEvento: event } : {}),
    ...(ip ? { ip } : {}),
  };
}
